import { useLocation, useNavigate } from '@tanstack/react-router';
import { FloatingWindow, Icon, IconButton, LoadingSpinner } from '@yembo/yemblocks-core';
import { ControlPosition } from 'react-draggable';
import { QueryJob } from 'src/queries/Job';
import { Customization } from 'src/queries/Company/customizations';
import { Employee, Company, Insured, Item, Room, SnapshotImage, Video } from 'src/types';
import { TextMessageContext } from '../_contexts/TextMessageContext';
import { getSurveyPath } from '../_routes/_util/surveyPath';
import { getFullName } from '../_util/getFullName';
import { TextMessages } from '../TextMessages';

type FloatingChatBoxProps = {
  job: QueryJob | undefined;
  rooms: Room[] | undefined;
  videos: Video[] | undefined;
  images: SnapshotImage[] | undefined;
  items: Item[] | undefined;
  insured: Insured | undefined;
  jobCompany: Company | undefined;

  customizations: Customization[] | undefined;
  closeChat: () => void;
  defaultPosition?: ControlPosition;
  employee: Employee | undefined;
};

export const CHAT_BOX_WIDTH = 500;
export const CHAT_BOX_HEIGHT = 600;

export const FloatingChatBox = ({
  job,
  rooms,
  videos,
  images,
  items,
  jobCompany,
  insured,

  defaultPosition,
  customizations,
  closeChat,
  employee,
}: FloatingChatBoxProps) => {
  const content =
    !job ||
    !rooms ||
    !videos ||
    !images ||
    !items ||
    !jobCompany ||
    !insured ||
    (!employee && job.employeeKey) ||
    !customizations ? (
      <div className='loading-wrapper'>
        <LoadingSpinner size='large' />
      </div>
    ) : (
      <TextMessageContext.Provider
        value={{
          job,
          rooms,
          videos,
          images,
          items,
          jobCompany,
          insured,

          customizations,
          employee,
        }}
      >
        <TextMessages key={job.key} />
      </TextMessageContext.Provider>
    );

  const location = useLocation();
  const navigate = useNavigate();

  const showGoToJobIcon = job && !location.pathname.includes(job.key);

  const header = (
    <div className='floating-chat-header'>
      <span>{`Chat with ${getFullName(insured ?? null)}`}</span>
      {showGoToJobIcon && (
        <Icon
          name='window-popout'
          color='white'
          onClick={() => {
            const pathname = `${getSurveyPath(job.key)}/overview`;
            navigate({ to: pathname });
          }}
        />
      )}
    </div>
  );

  return (
    <FloatingWindow
      bounds='parent'
      handle='.header'
      cancel='.no-dragging'
      defaultClassName='floating-chat-box'
      leftHeader={header}
      rightHeader={
        <IconButton
          kind='flat'
          onClick={closeChat}
          className='header-button'
          icon={<Icon name='x' color='white' className='no-dragging' />}
        />
      }
      width={CHAT_BOX_WIDTH}
      height={CHAT_BOX_HEIGHT}
      minWidth={350}
      minHeight={350}
      maxWidth={900}
      maxHeight={720}
      resizeMode='arbitrary'
      defaultPosition={defaultPosition}
    >
      {content}
    </FloatingWindow>
  );
};
